/*
 * bugsnag-lite.js — sender + breadcrumbs for the inline capture stub
 * (snippet bugsnag-lite.liquid). Loaded `defer`; served minified + gzipped +
 * cached by Shopify's CDN. Reads the window.__bugsnag buffer the stub fills,
 * records a privacy-safe breadcrumb trail, and POSTs ONE batched payload to
 * Bugsnag's ingest API via sendBeacon — only if errors occurred.
 *
 * Privacy: breadcrumbs record element DESCRIPTORS, request URLs (query stripped)
 * and HTTP statuses only — never input values, form data, or request/response
 * bodies. No PII is forwarded to Bugsnag.
 */
(() => {
  'use strict';

  const store = window.__bugsnag;
  if (!store || store.ready) return; // stub must have run; guard double-init
  store.ready = true;

  const API_KEY = 'c80483c280435690007eb73f604e7f75';
  const ENDPOINT = 'https://notify.bugsnag.com/';
  const MAX_CRUMBS = 20;
  const MAX_BODY = 60000; // stay under sendBeacon's ~64KB limit
  const crumbs = store.crumbs;

  // ---- breadcrumbs: descriptors only, never values/bodies (no PII) ----------
  const crumb = (type, name, meta = {}) => {
    crumbs.push({ t: new Date().toISOString(), type, name, meta });
    if (crumbs.length > MAX_CRUMBS) crumbs.shift();
  };

  const safeUrl = (u) => {
    try { const x = new URL(u, location.origin); return x.origin + x.pathname; }
    catch { return String(u ?? '').split('?')[0]; }
  };

  const describe = (el) => {
    if (!el?.tagName) return '';
    let s = el.tagName.toLowerCase();
    if (el.id) s += `#${el.id}`;
    if (typeof el.className === 'string' && el.className.trim()) {
      s += '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.');
    }
    return s;
  };

  // click (label from aria-label / text — inputs expose neither here)
  addEventListener('click', (ev) => {
    const t = ev.target;
    const label = t?.getAttribute?.('aria-label') || t?.textContent?.trim().slice(0, 40) || '';
    crumb('user', 'click', { el: describe(t), label });
  }, true);

  // navigation (SPA-style history + hash)
  for (const m of ['pushState', 'replaceState']) {
    const orig = history[m];
    if (typeof orig === 'function') {
      history[m] = function (...args) {
        crumb('navigation', m, { to: safeUrl(location.href) });
        return orig.apply(this, args);
      };
    }
  }
  addEventListener('popstate', () => crumb('navigation', 'popstate', { to: safeUrl(location.href) }));
  addEventListener('hashchange', () => crumb('navigation', 'hashchange', { to: safeUrl(location.href) }));

  // fetch (method + url + status — never bodies)
  if (window.fetch) {
    const orig = window.fetch;
    window.fetch = function (...args) {
      const [input, init] = args;
      const url = typeof input === 'string' ? input : input?.url ?? '';
      const method = init?.method ?? input?.method ?? 'GET';
      return orig.apply(this, args).then(
        (res) => { crumb('request', 'fetch', { method, url: safeUrl(url), status: res?.status }); return res; },
        (err) => { crumb('request', 'fetch failed', { method, url: safeUrl(url) }); throw err; }
      );
    };
  }

  // ---- stacktrace parsing ----------------------------------------------------
  const parseStack = (stack) => {
    if (!stack) return [];
    const frames = [];
    for (const raw of String(stack).split('\n')) {
      if (frames.length >= 30) break;
      const line = raw.trim();
      // Chrome/Edge: "at method (file:line:col)" | "at file:line:col"
      // Firefox/Safari: "method@file:line:col" | "@file:line:col" | "file:line:col"
      const m = line.match(/^at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/)
             || line.match(/^(?:(.*?)@)?(.+?):(\d+):(\d+)$/);
      if (m?.[2]) {
        frames.push({ method: m[1] || '(anonymous)', file: m[2], lineNumber: +m[3], columnNumber: +m[4] });
      }
    }
    return frames;
  };

  // ---- payload ---------------------------------------------------------------
  const buildEvent = ({ err, meta }) => {
    const rawStack = err?.stack ? String(err.stack).slice(0, 3000) : null;
    let frames = parseStack(rawStack);
    if (!frames.length && meta.file) {
      frames = [{ file: meta.file, lineNumber: meta.line || 0, columnNumber: meta.col || 0, method: '(global)' }];
    }
    return {
      exceptions: [{
        errorClass: err?.name || 'Error',
        message: (err?.message || String(err)).slice(0, 1000),
        stacktrace: frames,
      }],
      severity: 'error',
      unhandled: !meta.handled,
      context: location.pathname,
      app: { releaseStage: 'production', version: store.v || '' },
      device: { userAgent: navigator.userAgent, time: new Date().toISOString() },
      request: { url: safeUrl(location.href) },
      metaData: {
        diagnostics: {
          handled: !!meta.handled,
          rejection: !!meta.rejection,
          rawStack,
          viewport: `${innerWidth}x${innerHeight}`,
          connection: navigator.connection?.effectiveType ?? null,
          deviceMemory: navigator.deviceMemory ?? null,
        },
        breadcrumbs: [...crumbs],
      },
    };
  };

  const send = (body) => {
    try { if (navigator.sendBeacon?.(ENDPOINT, body)) return; } catch { /* fall through */ }
    try { fetch(ENDPOINT, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }); }
    catch { /* monitoring must never break the page */ }
  };

  const flush = () => {
    const batch = store.errors.splice(0, store.errors.length);
    if (!batch.length) return;
    const payload = {
      apiKey: API_KEY,
      payloadVersion: '4',
      notifier: { name: 'smilemobile-lite', version: '1.0.0', url: 'https://smilemobile.ro' },
      events: batch.map(buildEvent),
    };
    let body = JSON.stringify(payload);
    // Oversized? Re-encode without the heavy fields rather than drop the report.
    if (body.length > MAX_BODY) {
      for (const e of payload.events) {
        e.metaData.breadcrumbs = [];
        if (e.metaData.diagnostics) e.metaData.diagnostics.rawStack = null;
      }
      body = JSON.stringify(payload);
    }
    send(body);
  };
  store.flush = flush; // pagehide / visibility triggers in the stub call this

  // ---- idle flush: send while the user is still here, not only on exit -------
  let scheduled = false;
  const scheduleIdle = () => {
    if (scheduled || !store.errors.length) return;
    scheduled = true;
    const run = () => { scheduled = false; flush(); };
    'requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 2000);
  };

  // resource-load failures → breadcrumb (errors themselves are buffered by the stub)
  addEventListener('error', (ev) => {
    const el = ev.target;
    if (!ev.error && el && (el.src || el.href)) crumb('error', 'resource failed', { url: safeUrl(el.src || el.href) });
    scheduleIdle();
  }, true);
  addEventListener('unhandledrejection', scheduleIdle);

  // drain anything the stub buffered before this file ran
  if (document.readyState === 'complete') scheduleIdle();
  else addEventListener('load', scheduleIdle, { once: true });
})();
